/**
 * Every word on the site, in one place.
 *
 * -------------------------------------------------------------------------
 * HOW TO EDIT
 *
 * Change the text between the quote marks. Nothing else.
 *
 *   title: 'Creating products people love and workplaces they thrive in.',
 *                ^ edit inside here                                    ^
 *
 * Four rules:
 *
 *   1. Keep the quote marks at both ends, and the comma at the end.
 *   2. If your text contains an apostrophe, use the curly one — ’ not ' —
 *      or the quote marks will end early and the site will not build.
 *      Copying an apostrophe from existing text here is the safest way.
 *   3. Write & and < and > normally. They are escaped automatically.
 *   4. A list in [square brackets] is a sequence of paragraphs or items.
 *      Add one by copying an entire { ... }, block, comma included.
 *
 * After editing, run `npm test` from the client folder. It checks that the
 * file is still valid and that every page still renders. Then build and
 * deploy as usual.
 *
 * -------------------------------------------------------------------------
 * WHAT IS NOT HERE
 *
 * Articles are Markdown files in client/public/posts/ — edit those directly.
 *
 * Interface state messages ("Loading…", "Try again", pagination labels) stay
 * with the components that drive those states, because the wording and the
 * behaviour have to change together.
 */

export const site = {
  name: 'Vagelis Antoniadis',
  navigation: [
    { to: '/', label: 'Home', end: true },
    { to: '/insights', label: 'Insights' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ],
};

/** Shared by the Home and About pages, which show the same portrait. */
export const portraitAlt =
  'Vagelis Antoniadis, in a black-and-white portrait lit from one side against a dark background';

export const home = {
  hero: {
    title: 'Creating products people love and workplaces they thrive in.',
    lead: 'A software engineer turned product leader—bridging technical ambition and practical strategy to build a culture of deep ownership, collaboration, and shared joy in creating unique products.',
    primaryAction: { label: 'Explore insights', to: '/insights' },
    secondaryAction: { label: 'Get in touch', to: '/contact' },
  },

  intro: {
    eyebrow: 'A practical perspective',
    title: 'From complex engineering to a clear product direction.',
    paragraphs: [
      'Vagelis is the CEO and co-founder of Cytech. After starting as a software engineer, he has spent the last decade focused on product development and engaging, radically human workplaces—turning technical capabilities into clear product visions and roadmaps.',
      'He also mentors teams as they build the habits that let them ship well and bridge the gap between complex engineering and practical business goals.',
    ],
    link: { label: 'More about Vagelis', to: '/about' },
  },

  expertise: {
    eyebrow: 'Areas of focus',
    title: 'Building the product and the environment around it.',
    areas: [
      {
        number: '01',
        title: 'Product vision & strategy',
        description: 'Translating technical capabilities into product visions, practical roadmaps, and business direction.',
      },
      {
        number: '02',
        title: 'Product development',
        description: 'Guiding teams that build software products across telecommunications, sports, tourism, and education.',
      },
      {
        number: '03',
        title: 'Team craft & mentorship',
        description: 'Helping teams connect engineering decisions with practical business goals and the habits that make teams work well.',
      },
    ],
  },

  recentInsights: {
    eyebrow: 'Recent insights',
    title: 'Ideas from the field.',
    allLink: { label: 'View all insights', to: '/insights' },
  },

  contact: {
    eyebrow: 'Continue the conversation',
    title: 'Have a product challenge worth exploring?',
    body: 'Connect with Vagelis to talk about software product development, what makes teams work, or the space where technology and business meet.',
    action: { label: 'Get in touch', to: '/contact' },
  },
};

export const about = {
  hero: {
    eyebrow: 'About Vagelis',
    title: 'Building a clear path from engineering to product strategy.',
    lead: 'Vagelis Antoniadis is a software engineer turned product and business leader, focused on product development, business strategy, and connecting technical capabilities with practical business goals.',
    primaryAction: { label: 'Read the insights', to: '/insights' },
    secondaryAction: { label: 'Get in touch', to: '/contact' },
  },

  story: {
    eyebrow: 'The journey',
    title: 'More than two decades of building software and the teams behind it.',
    paragraphs: [
      'Vagelis is the CEO and co-founder of Cytech, a software development company established more than two decades ago with a strong foundation in telecommunications.',
      'He began his career as a software engineer. Over the last ten years, his focus has moved toward product development and business strategy—guiding teams as they build products and turning technical capabilities into successful product visions and roadmaps.',
      'He has also participated actively in software and product communities for more than a decade. Mentorship is a meaningful part of that work: helping teams build the habits that let them ship well and bridge the gap between complex engineering and practical business goals.',
    ],
  },

  focus: {
    eyebrow: 'Areas of focus',
    title: 'The product, the strategy, and the environment around both.',
    areas: [
      {
        number: '01',
        title: 'Product vision & strategy',
        description: 'Translating technical capabilities into clear product visions, roadmaps, and practical business direction.',
      },
      {
        number: '02',
        title: 'Software product development',
        description: 'Guiding teams as they build products across telecommunications, sports, tourism, and education.',
      },
      {
        number: '03',
        title: 'Team craft & mentorship',
        description: 'Helping teams connect complex engineering with practical business goals and the habits that make teams work well.',
      },
    ],
  },

  personal: {
    eyebrow: 'Beyond the work',
    title: 'Curiosity does not stop at software.',
    paragraphs: [
      'Away from leadership and mentorship, Vagelis is passionate about the art of bread making, enjoys discovering new wines, and likes to unwind with a good Dungeons & Dragons game.',
      'This blog gathers his writing on products, technology, leadership, and the ways teams work together.',
    ],
    link: { label: 'Explore the latest writing', to: '/insights' },
  },

  contact: {
    eyebrow: 'Continue the conversation',
    title: 'Have a product challenge worth exploring?',
    body: 'Connect with Vagelis to talk about software product development, what makes teams work, or the space where technology and business meet.',
    action: { label: 'Get in touch', to: '/contact' },
  },
};

export const insights = {
  eyebrow: 'Insights',
  title: 'Ideas for building products and teams that thrive.',
  lead: 'Writing on product strategy, software, leadership, and the systems that help people do meaningful work.',
};

export const contact = {
  hero: {
    eyebrow: 'Contact',
    title: 'Let’s start a conversation.',
    lead: 'Whether you want to discuss software product development, what makes teams work, or simply connect, I’d love to hear from you.',
  },

  direct: {
    eyebrow: 'Direct inquiries',
    title: 'The best place to begin is email.',
    body: 'Send a note and include a little context about what you’d like to discuss.',
    emailLabel: 'Email Vagelis',
    email: 'contact@vanton.me',
  },

  social: {
    eyebrow: 'Elsewhere',
    title: 'Social media & code',
    body: 'Follow my work, open-source contributions, and thoughts on technology.',
    links: [
      { label: 'LinkedIn', handle: 'vanton', href: 'https://www.linkedin.com/in/vanton/' },
      { label: 'Facebook', handle: '@vagelis.antoniadis', href: 'https://www.facebook.com/vagelis.antoniadis/' },
      { label: 'Twitter / X', handle: '@vanton_', href: 'https://twitter.com/vanton_' },
      { label: 'GitHub', handle: 'vanton', href: 'https://github.com/vanton1' },
      { label: 'Medium', handle: '@vanton_', href: 'https://medium.com/@vanton_' },
    ],
  },

  location: {
    eyebrow: 'Based in Greece',
    title: 'Athens & Heraklion, Crete',
    paragraphs: [
      'I split my time between Athens and Heraklion, where my company, Cytech, is headquartered.',
      'For business inquiries related to mobile technology solutions or Cytech, visit the company website.',
    ],
    link: { label: 'Visit Cytech', href: 'https://cytechmobile.com' },
  },
};

export const footer = {
  brand: 'Vagelis Antoniadis',
  statement: 'Creating products people love and workplaces they thrive in.',
  navigation: [
    { to: '/insights', label: 'Insights' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ],
  copyright: {
    startYear: 2013,
    holder: 'Vagelis Antoniadis',
    // Shown until the browser resolves the live year. See SiteFooter.js for
    // why the year cannot be read while the page is first rendered.
    yearBeforeLoad: 'present',
  },
};
